import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import type {
  GetInvestmentBalancesAtQuery,
  GetInvestmentBalancesAtQueryVariables,
} from './__generated__/useInvestmentBalancesAt.gql';

// A second document on the list's root field rather than a selection on the shared list query:
// that query takes no variables and every account surface runs it.
const GET_INVESTMENT_BALANCES_AT = gql`
  query GetInvestmentBalancesAt($date: DateTimeISO!) {
    getFinancialAccountsForUser {
      id
      balanceAt(date: $date) {
        id
        balance
        timestamp
      }
    }
  }
`;

interface IUseInvestmentBalancesAtOptions {
  skip: boolean;
}

/**
 * Each account's recorded value at or before `date`, keyed by account id.
 *
 * The server answers for investment accounts only; every other account, and an investment
 * account with no snapshot that old, maps to null. The map is empty while skipped, loading
 * without a cached result, or on error, so a caller reads "no past value" rather than zero.
 *
 * @param date Keep it stable across renders: it is the query variable and the cache key.
 * @param options.skip `true` issues no request and yields the empty map.
 * @returns Account id to its value at or before `date`, or to null where the server had none.
 */
export const useInvestmentBalancesAt = (
  date: Date,
  { skip }: IUseInvestmentBalancesAtOptions
): ReadonlyMap<string, number | null> => {
  // A snapshot at or before a fixed past instant does not change, so the cache is authoritative.
  const { data } = useQuery<GetInvestmentBalancesAtQuery, GetInvestmentBalancesAtQueryVariables>(
    GET_INVESTMENT_BALANCES_AT,
    { variables: { date: date.toISOString() }, skip, fetchPolicy: 'cache-first' }
  );

  return React.useMemo(() => {
    const balances = new Map<string, number | null>();
    for (const account of data?.getFinancialAccountsForUser ?? []) {
      balances.set(account.id, account.balanceAt ? parseFloat(account.balanceAt.balance) : null);
    }
    return balances;
  }, [data]);
};
